import type {
  CompRef,
  ScrubAnimationDef,
  TriggerVariant,
} from 'types/documentServices';

import type { ComponentEditorMetaDataDefinition } from '#packages/componentModel';
import { componentsStore, selection, preview } from '#packages/stateManagement';
import constants from '#packages/constants';
import { array } from '#packages/util';

import type { AnimationsScope } from '../../scope';
import type { EffectData, MouseEffectData, ScrollEffectData } from '../types';
import { triggerParams } from './configs';
import type { ScrubAnimationType } from './types';

interface PreviewScrubAnimationData {
  animations: Record<string, ScrubAnimationDef>;
  triggers: Record<string, TriggerVariant>;
}

export const createPreviewApi = ({
  editorAPI,
  helpers,
  store,
}: AnimationsScope) => {
  const previewIds = new Map<string, string | number | null>();
  const defaultCompRestrictions = new Map<
    string,
    ComponentEditorMetaDataDefinition
  >();

  function start(compRef: CompRef, effectObj: EffectData) {
    const onComplete = () => {
      previewIds.delete(compRef.id);
    };
    const animationData = {
      ...effectObj.value,
      params: effectObj.value.namedEffect,
    };

    const previewId = editorAPI.components.motion.play(
      compRef.id,
      { ...animationData, iterations: 1 },
      onComplete,
    )?.options.effectId;

    previewIds.set(compRef.id, previewId);
  }

  function stop(compRef: CompRef) {
    editorAPI.components.motion.cancel(previewIds.get(compRef.id) as string);
  }

  function getScrubPreviewData(
    compRefs: CompRef[],
    type: ScrubAnimationType,
    effectObj: ScrollEffectData | MouseEffectData,
  ) {
    return compRefs.reduce(
      (acc, compRef) => {
        const { defaultCompRef, compRefWithMobileVariant } =
          helpers.getCompRefs(compRef);
        const isAnimationInMobileVariant =
          compRef.type === editorAPI.viewMode.VIEW_MODES.MOBILE &&
          helpers.hasMobileOverrides(compRefWithMobileVariant);
        const compToUpdate = isAnimationInMobileVariant
          ? compRefWithMobileVariant
          : defaultCompRef;

        const effectRef = helpers.findReaction(compToUpdate, type)?.effect;
        const effectData = helpers.getEffectData(compToUpdate, effectRef);
        const triggerNameAndParams = triggerParams[type]();

        acc.animations[effectRef.id] = {
          ...effectData.value,
          ...effectObj.value,
          targetId: compToUpdate.id,
        };
        acc.triggers[effectRef.id] = {
          ...helpers.findTriggerRef(compToUpdate, triggerNameAndParams.trigger),
          type: 'Trigger',
          componentId: compToUpdate.id,
          ...triggerNameAndParams,
        };

        return acc;
      },
      { animations: {}, triggers: {} } as PreviewScrubAnimationData,
    );
  }

  function startScrubPreview(
    compRef: CompRef | CompRef[],
    type: ScrubAnimationType,
    effectObj: ScrollEffectData | MouseEffectData,
  ) {
    const compRefs = array.asArray(compRef);

    compRefs.forEach((compRef) => {
      defaultCompRestrictions.set(
        compRef.id,
        editorAPI.getCompRestrictions(compRef),
      );

      editorAPI.components.layout.stage.freeze(compRef);

      const newRestrictions = {
        ...defaultCompRestrictions.get(compRef.id),
        // it would be better to not use `horizontally-` and `vertically-` keys,
        // but it seems like selection box only takes them into account
        movable: false,
        horizontallyMovable: false,
        verticallyMovable: false,
        resizable: false,
        verticallyResizable: false,
        horizontallyResizable: false,
        rotatable: false,
        verticallyRotatable: false,
        horizontallyRotatable: false,
      };

      editorAPI.store.dispatch(
        componentsStore.actions.updateComponentStore(
          compRef,
          constants.COMPONENTS_STORE_KEYS.META_DATA_OVERRIDES,
          newRestrictions,
        ),
      );

      editorAPI.store.dispatch(
        selection.actions.setSelectedCompsRestrictions(newRestrictions),
      );

      editorAPI.store.dispatch(preview.actions.setPreviewPointerEventsOn(true));
    });

    const { animations, triggers } = getScrubPreviewData(
      compRefs,
      type,
      effectObj,
    );
    editorAPI.components.motion.startScrub(triggers, animations);
    store.setIsInScrubPreview(true);
  }

  async function stopScrubPreview(compRef: CompRef | CompRef[]) {
    const compRefs = array.asArray(compRef);
    const onComplete = () => {
      compRefs.forEach((compRef) => {
        editorAPI.components.layout.stage.unfreeze(compRef);

        editorAPI.store.dispatch(
          componentsStore.actions.updateComponentStore(
            compRef,
            constants.COMPONENTS_STORE_KEYS.META_DATA_OVERRIDES,
            defaultCompRestrictions.get(compRef.id),
          ),
        );

        editorAPI.store.dispatch(
          selection.actions.setSelectedCompsRestrictions(
            defaultCompRestrictions.get(compRef.id),
          ),
        );

        defaultCompRestrictions.delete(compRef.id);

        editorAPI.store.dispatch(
          preview.actions.setPreviewPointerEventsOn(false),
        );
      });
    };

    editorAPI.components.motion.cancelScrub(onComplete);
    store.setIsInScrubPreview(false);
  }

  function updateScrubPreviewData(
    compRef: CompRef | CompRef[],
    type: ScrubAnimationType,
    effectObj: ScrollEffectData | MouseEffectData,
  ) {
    const { animations, triggers } = getScrubPreviewData(
      array.asArray(compRef),
      type,
      effectObj,
    );
    editorAPI.components.motion.updateScrub(triggers, animations);
  }

  return {
    start: (compRefs: CompRef | CompRef[], effectData: EffectData) =>
      array.applyForAll((compRef) => start(compRef, effectData), compRefs),
    stop: (compRefs: CompRef | CompRef[]) =>
      array.applyForAll((compRef) => stop(compRef), compRefs),
    scrub: {
      start: startScrubPreview,
      stop: stopScrubPreview,
      update: updateScrubPreviewData,
    },
  };
};
